/**
 * PropertyOffersPage
 *
 * Displays all offers on a property for agents to view and manage.
 * Uses DataTable for easy comparison of offers.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams, useSearch, useNavigate, Link, Navigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { useToast } from '@/common/hooks/useToast'
import { useMediaQueryToken } from '@/common/hooks/useMediaQuery'
import { Button } from '@/common/components/button'
import { Badge } from '@/common/components/badge'
import { Icon } from '@/common/components/icon'
import { Text } from '@/common/components/text'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { DataTable } from '@/common/components/data-table'
import { Dialog } from '@/common/components/dialog'
import { DropdownMenu } from '@/common/components/dropdown-menu'
import { ToggleGroup } from '@/common/components/toggle-group'
import { getErrorMessage } from '@/common/utils/errors'
import {
  useOffersSource,
  acceptOffer,
  rejectOffer,
  engageOffer,
  submitCounterOffer,
  getOffer,
  CounterOfferDialog,
  OfferDetailDialog,
  AutoBidBadge,
  type OfferStatus,
  type OfferWithPropertyResponse,
  type CounterOfferCreate,
} from '@/features/offers'
import { resolveContractVariant } from '@/features/offers/utils/contractVariant'
import type { DataView } from '@/core/data'
import { getProperty } from '@/features/properties/api/properties'
import { useAgentTrainingMode } from '@/features/agent-training-tour'
import { pageContainer } from '@/core/styles'
import * as styles from './offers.css'

export const Route = createFileRoute({
  component: PropertyOffersPage,
  validateSearch: (search: Record<string, unknown>) => ({
    offerId: typeof search.offerId === 'string' ? search.offerId : undefined,
  }),
})

// =============================================================================
// Constants
// =============================================================================

const statusColor: Record<OfferStatus, 'info' | 'success' | 'caution' | 'error' | 'neutral'> = {
  pending: 'caution',
  engaged: 'info',
  accepted: 'success',
  rejected: 'error',
  withdrawn: 'neutral',
}

const statusLabels: Record<OfferStatus, string> = {
  pending: 'Pending',
  engaged: 'Engaged',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatClosingTimeline(timeline: string | null | undefined): string {
  if (!timeline) return '—'
  switch (timeline) {
    case '10_days':
      return '10 days'
    case '14_days':
      return '14 days'
    case '30_days':
      return '30 days'
    case '30_60_days':
      return '30-60 days'
    case 'flexible':
      return 'Flexible'
    default:
      return timeline
  }
}

function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// =============================================================================
// Component
// =============================================================================

function PropertyOffersPage() {
  // Params can come back empty after the post-login client-side redirect (same TanStack quirk as properties/$id/index.tsx)
  const params = useParams({ strict: false }) as { id?: string }
  const id = params.id ?? window.location.pathname.match(/\/properties\/([^/]+)/)?.[1]
  const { offerId: linkedOfferId } = useSearch({ from: '/_auth/properties/$id/offers' })
  const navigate = useNavigate()
  const { token, roles, isLoading: authLoading } = useAuth()
  const { data: userProfile } = useUserProfile()
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()
  const isMobile = useMediaQueryToken('largePhoneDown')
  const [processingOfferId, setProcessingOfferId] = useState<string | null>(null)
  const [selectedStatuses, setSelectedStatuses] = useState<OfferStatus[]>(['pending', 'engaged', 'accepted'])

  // Separate open state from data to preserve content during close animation
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [detailDialogOffer, setDetailDialogOffer] = useState<OfferWithPropertyResponse | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [confirmAction, setConfirmAction] = useState<{ type: 'accept' | 'reject' | 'engage'; offer: OfferWithPropertyResponse } | null>(null)
  const [counterOfferDialogOpen, setCounterOfferDialogOpen] = useState(false)
  const [counterOfferTarget, setCounterOfferTarget] = useState<OfferWithPropertyResponse | null>(null)

  const openDetailDialog = (offer: OfferWithPropertyResponse) => {
    setDetailDialogOffer(offer)
    setDetailDialogOpen(true)
  }

  const openConfirmDialog = (type: 'accept' | 'reject' | 'engage', offer: OfferWithPropertyResponse) => {
    setConfirmAction({ type, offer })
    setConfirmDialogOpen(true)
  }

  const isAgent = roles?.includes('agent')
  const isAdmin = roles?.includes('admin')
  const canManageOffers = isAgent || isAdmin
  const contractVariant = resolveContractVariant({
    roles,
    agentType: userProfile?.profile?.agent_type,
  })

  // Ref to access the DataTable's internal view (for refetch after accept/reject)
  const viewRef = useRef<DataView<ReturnType<typeof useOffersSource>> | null>(null)

  // MRG-281: in-training external agents see hardcoded fixtures so the
  // guided tour has data to walk through without touching real DB rows.
  const trainingMode = useAgentTrainingMode()

  // Fetch property for header
  const { data: property, isLoading: propertyLoading } = useQuery({
    queryKey: ['property', id, trainingMode],
    queryFn: () => getProperty(token!, id!, { trainingMode }),
    enabled: !!token && !!id,
  })

  // Offers data source with property filter baked in
  const offersSource = useOffersSource({
    filters: id ? { propertyIds: [id] } : undefined,
    trainingMode,
  })

  // Deep-link support: when `?offerId=...` is present (e.g. from the
  // invoicing-team contract-upload email), fetch that offer and auto-open
  // the detail dialog once. Closing the dialog clears the query param so
  // refreshing doesn't reopen it. Gated on agent/admin so investors who
  // land here aren't billed an extra `/offers/{id}` request before the
  // redirect below kicks them to the property details page.
  const { data: linkedOffer } = useQuery({
    queryKey: ['offer', linkedOfferId],
    queryFn: () => getOffer(token!, linkedOfferId!),
    enabled: !!token && !!linkedOfferId && (isAgent || isAdmin),
  })

  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (linkedOffer && !autoOpenedRef.current) {
      autoOpenedRef.current = true
      setDetailDialogOffer(linkedOffer)
      setDetailDialogOpen(true)
    }
  }, [linkedOffer])

  const handleDetailDialogOpenChange = (open: boolean) => {
    setDetailDialogOpen(open)
    if (!open && linkedOfferId && id) {
      navigate({
        to: '/properties/$id/offers',
        params: { id },
        search: { offerId: undefined },
        replace: true,
      })
    }
  }

  // Track if this is the initial render (skip setFilters since initialFilters handles it)
  const isInitialRender = useRef(true)

  // Apply status filter when selection changes (not on initial render)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    viewRef.current?.setFilters({ statuses: selectedStatuses })
  }, [selectedStatuses])

  const handleAccept = useCallback(
    async (offer: OfferWithPropertyResponse) => {
      if (!token || processingOfferId) return

      setProcessingOfferId(offer.id)
      try {
        await acceptOffer(token, offer.id)
        showSuccessToast('Offer accepted!')
        viewRef.current?.refetch()
        // Invalidate property cache since accepting an offer may change property status
        queryClient.invalidateQueries({ queryKey: ['property', id] })
        // Invalidate offers DataSource cache (used by property details page)
        queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to accept offer')
      } finally {
        setProcessingOfferId(null)
      }
    },
    [token, processingOfferId, showSuccessToast, showErrorToast, queryClient, id]
  )

  const handleReject = useCallback(
    async (offer: OfferWithPropertyResponse) => {
      if (!token || processingOfferId) return

      setProcessingOfferId(offer.id)
      try {
        await rejectOffer(token, offer.id)
        showSuccessToast('Offer rejected')
        viewRef.current?.refetch()
        // Invalidate offers DataSource cache (used by property details page)
        queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to reject offer')
      } finally {
        setProcessingOfferId(null)
      }
    },
    [token, processingOfferId, showSuccessToast, showErrorToast, queryClient]
  )

  const handleEngage = useCallback(
    async (offer: OfferWithPropertyResponse) => {
      if (!token || processingOfferId) return

      setProcessingOfferId(offer.id)
      try {
        await engageOffer(token, offer.id)
        showSuccessToast('Offer engaged!')
        viewRef.current?.refetch()
        queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
      } catch (err) {
        showErrorToast(getErrorMessage(err) || 'Failed to engage offer')
      } finally {
        setProcessingOfferId(null)
      }
    },
    [token, processingOfferId, showSuccessToast, showErrorToast, queryClient]
  )

  const handleCounterOffer = useCallback(
    async (offerId: string, data: CounterOfferCreate) => {
      if (!token) return
      await submitCounterOffer(token, offerId, data)
      showSuccessToast('Counter-offer submitted!')
      viewRef.current?.refetch()
      queryClient.invalidateQueries({ queryKey: ['dataSource', 'offers'] })
      queryClient.invalidateQueries({ queryKey: ['negotiations', offerId] })
    },
    [token, showSuccessToast, queryClient]
  )

  // Only agents and admins can view this page - redirect investors to property details
  const isAgentOrAdmin = roles?.includes('agent') || roles?.includes('admin')
  if (!isAgentOrAdmin && id) {
    return <Navigate to="/properties/$id" params={{ id }} replace />
  }

  // Loading state — also wait while auth hydrates. Without the `authLoading`
  // and `!token` checks, a user arriving via email deep-link (or any fresh
  // tab) would briefly render "Property Not Found" because the property
  // query is gated on `!!token` and `propertyLoading` is `false` for a
  // disabled query.
  if (authLoading || !token || propertyLoading) {
    return (
      <div className={pageContainer({ width: 'full' })}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state
  if (!property) {
    return (
      <div className={pageContainer({ width: 'full' })}>
        <Link to="/properties" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back to Properties
        </Link>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="bodyLg" weight="bold">Property Not Found</Text>
          <Text color="muted">
            The property you're looking for doesn't exist or you don't have
            permission to view it.
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className={pageContainer({ width: 'full' })}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <Link to="/properties/$id" params={{ id: property.id }} className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back to property
        </Link>
      </div>

      {/* Property info */}
      <div className={styles.propertyInfo}>
        <div className={styles.addressSection}>
          <Text as="h1" variant="h4" weight="bold">
            {property.address?.street_address}
            {property.address?.unit_number && ` ${property.address.unit_number}`}
          </Text>
          <Text color="muted">
            {property.address?.city}, {property.address?.state} {property.address?.zipcode}
          </Text>
        </div>

        {/* Property stats for quick reference */}
        <div className={styles.propertyStats}>
          <div className={styles.statItem}>
            <Text variant="bodyXs" color="muted">Recommended List Price</Text>
            <Text variant="bodySm" weight="bold">{property.market_value != null ? formatCurrency(property.market_value) : '—'}</Text>
          </div>
          <div className={styles.statItem}>
            <Text variant="bodyXs" color="muted">Floor Price</Text>
            <Text variant="bodySm" weight="bold">
              {property.floor_price ? formatCurrency(property.floor_price) : '—'}
            </Text>
          </div>
          <div className={styles.statItem}>
            <Text variant="bodyXs" color="muted">Closing Timeline</Text>
            <Text variant="bodySm" weight="bold">{formatClosingTimeline(property.closing_timeline)}</Text>
          </div>
          <div className={styles.statItem}>
            <Text variant="bodyXs" color="muted">Offer Deadline</Text>
            <Text variant="bodySm" weight="bold">
              {property.offer_deadline ? formatDate(property.offer_deadline) : 'No deadline'}
            </Text>
          </div>
        </div>
      </div>

      {/* Offers Table */}
      <div className={styles.offersSection} data-tour-step="agent-offers-list">
        <DataTable
          source={offersSource}
          viewRef={viewRef}
          getRowKey={(offer) => offer.id}
          aria-label="Offers on this property"
          getRowLabel={(offer) => `Offer from ${offer.investor?.first_name ?? 'Unknown'} ${offer.investor?.last_name ?? ''}, ${offer.status}`}
          initialSort={{ field: 'offer_price', direction: 'desc' }}
          initialFilters={{ statuses: selectedStatuses }}
          additionalFields={['investor', 'earnest_money_deposit', 'notes', 'closing_timeline', 'created_at']}
          emptyMessage="No offers on this property yet"
          pinnedLast
          onRowClick={(offer) => openDetailDialog(offer)}
        >
          <DataTable.Toolbar>
            <ToggleGroup
              type="multiple"
              value={selectedStatuses}
              onValueChange={(value) => setSelectedStatuses(value as OfferStatus[])}
              variant="pills"
            >
              <ToggleGroup.Item value="pending">Pending</ToggleGroup.Item>
              <ToggleGroup.Item value="engaged">Engaged</ToggleGroup.Item>
              <ToggleGroup.Item value="accepted">Accepted</ToggleGroup.Item>
              <ToggleGroup.Item value="rejected">Rejected</ToggleGroup.Item>
              <ToggleGroup.Item value="withdrawn">Withdrawn</ToggleGroup.Item>
            </ToggleGroup>
          </DataTable.Toolbar>
          <DataTable.Columns>
            <DataTable.Column
              id="investor"
              header="Investor"
              field="investor"
            >
              {(offer: OfferWithPropertyResponse) => {
                const investor = offer.investor
                if (!investor) return <Text color="muted">Unknown</Text>
                const name = [investor.first_name, investor.last_name].filter(Boolean).join(' ')
                return (
                  <div>
                    <Text variant="bodySm">{name || 'Unknown'}</Text>
                    {investor.public_email && (
                      <Text variant="bodyXs">
                        <a href={`mailto:${investor.public_email}`} onClick={(e) => e.stopPropagation()}>
                          {investor.public_email}
                        </a>
                      </Text>
                    )}
                    {investor.company_name && (
                      <Text variant="bodyXs" color="muted">{investor.company_name}</Text>
                    )}
                  </div>
                )
              }}
            </DataTable.Column>

            <DataTable.Column
              id="offer_price"
              header="Offer Amount"
              sortable
            >
              {(offer: OfferWithPropertyResponse) => (
                <div className={styles.stackedCell}>
                  <Text weight="bold">{formatCurrency(offer.offer_price)}</Text>
                  {/* Submitted column is hidden on mobile */}
                  {isMobile && <AutoBidBadge source={offer.source} status={offer.status} variant="icon" />}
                </div>
              )}
            </DataTable.Column>

            {!isMobile && (
              <DataTable.Column
                id="earnest_money_deposit"
                header="EMD"
                field="earnest_money_deposit"
              >
                {(offer: OfferWithPropertyResponse) => (
                  <Text variant="bodySm">
                    {offer.earnest_money_deposit != null ? `${offer.earnest_money_deposit}%` : '—'}
                  </Text>
                )}
              </DataTable.Column>
            )}

            {!isMobile && (
              <DataTable.Column
                id="notes"
                header="Notes"
                field="notes"
              >
                {(offer: OfferWithPropertyResponse) => (
                  <Text variant="bodyXs" className={styles.notesCell}>
                    {offer.notes || '—'}
                  </Text>
                )}
              </DataTable.Column>
            )}

            <DataTable.Column
              id="status"
              header="Status"
              sortable
            >
              {(offer: OfferWithPropertyResponse) => (
                <Badge color={statusColor[offer.status]}>
                  {statusLabels[offer.status]}
                </Badge>
              )}
            </DataTable.Column>

            {!isMobile && (
              <DataTable.Column
                id="created_at"
                header="Submitted"
                sortable
              >
                {(offer: OfferWithPropertyResponse) => (
                  <div className={styles.submittedCell}>
                    <Text variant="bodySm">{formatDate(offer.created_at)}</Text>
                    <AutoBidBadge source={offer.source} status={offer.status} variant="icon" />
                  </div>
                )}
              </DataTable.Column>
            )}

            {canManageOffers && (
              <DataTable.Column
                id="actions"
                header=""
                field={false}
                align="right"
                width={48}
              >
                {(offer: OfferWithPropertyResponse) => {
                  const isPending = offer.status === 'pending'
                  const isEngaged = offer.status === 'engaged'
                  const canAct = isPending || isEngaged

                  if (!canAct) return null

                  return (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      data-tour-step="agent-offer-actions"
                    >
                      <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                          <Button variant="ghost" size="sm">
                            <Icon name="more_vert" size={20} />
                          </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align="end">
                          {isPending && (
                            <DropdownMenu.Item onSelect={() => openConfirmDialog('engage', offer)}>
                              Engage
                            </DropdownMenu.Item>
                          )}
                          {isEngaged && (
                            <DropdownMenu.Item onSelect={() => {
                              setCounterOfferTarget(offer)
                              setCounterOfferDialogOpen(true)
                            }}>
                              Counter Offer
                            </DropdownMenu.Item>
                          )}
                          <DropdownMenu.Item onSelect={() => openConfirmDialog('accept', offer)}>
                            Accept
                          </DropdownMenu.Item>
                          <DropdownMenu.Item danger onSelect={() => openConfirmDialog('reject', offer)}>
                            Reject
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu>
                    </div>
                  )
                }}
              </DataTable.Column>
            )}
          </DataTable.Columns>
        </DataTable>
      </div>

      {/* Offer Detail Modal */}
      <OfferDetailDialog
        open={detailDialogOpen}
        onOpenChange={handleDetailDialogOpenChange}
        offer={detailDialogOffer}
        token={token}
        contractVariant={contractVariant}
        actions={canManageOffers ? {
          onEngage: (offer) => {
            openConfirmDialog('engage', offer)
          },
          onAccept: (offer) => {
            openConfirmDialog('accept', offer)
          },
          onReject: (offer) => {
            openConfirmDialog('reject', offer)
          },
          onCounterOffer: (offer) => {
            setCounterOfferTarget(offer)
            setCounterOfferDialogOpen(true)
          },
        } : undefined}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <Dialog.Content width={400}>
          <Dialog.Header>
            <Dialog.Title>
              {confirmAction?.type === 'accept'
                ? 'Accept Offer'
                : confirmAction?.type === 'engage'
                  ? 'Engage Offer'
                  : 'Reject Offer'}
            </Dialog.Title>
            <Dialog.Close />
          </Dialog.Header>

          {confirmAction && (
            <>
              <Text>
                Are you sure you want to {confirmAction.type} the{' '}
                <Text as="span" weight="bold">{formatCurrency(confirmAction.offer.offer_price)}</Text>{' '}
                offer from{' '}
                <Text as="span" weight="bold">
                  {[confirmAction.offer.investor?.first_name, confirmAction.offer.investor?.last_name].filter(Boolean).join(' ') || 'Unknown'}
                </Text>
                ?
              </Text>

              <Dialog.Footer>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialogOpen(false)}
                  disabled={processingOfferId === confirmAction.offer.id}
                >
                  Cancel
                </Button>
                <Button
                  variant={confirmAction.type === 'reject' ? 'danger' : 'primary'}
                  onClick={async () => {
                    if (confirmAction.type === 'accept') {
                      await handleAccept(confirmAction.offer)
                    } else if (confirmAction.type === 'engage') {
                      await handleEngage(confirmAction.offer)
                    } else {
                      await handleReject(confirmAction.offer)
                    }
                    setConfirmDialogOpen(false)
                  }}
                  disabled={processingOfferId === confirmAction.offer.id}
                >
                  {processingOfferId === confirmAction.offer.id
                    ? '...'
                    : confirmAction.type === 'accept'
                      ? 'Accept'
                      : confirmAction.type === 'engage'
                        ? 'Engage'
                        : 'Reject'}
                </Button>
              </Dialog.Footer>
            </>
          )}
        </Dialog.Content>
      </Dialog>

      {/* Counter Offer Dialog */}
      {counterOfferTarget && (
        <CounterOfferDialog
          open={counterOfferDialogOpen}
          onOpenChange={setCounterOfferDialogOpen}
          offer={counterOfferTarget}
          onSubmit={handleCounterOffer}
        />
      )}
    </div>
  )
}
